import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

import { MAX_WORD_LENGTH, normaliseWord } from './spell-check-protocol';

export interface WordToken {
  word: string;
  /** Offsets in the text the token came from (end exclusive). */
  start: number;
  end: number;
}

/** Letters with internal apostrophes (don't, organisation's). Hyphens split words, as in Word. */
const WORD = /[\p{L}\p{M}]+(?:['\u2019][\p{L}\p{M}]+)*/gu;
const CHUNK = /\S+/g;
const SCHEME_URL = /[a-z][a-z0-9+.-]*:\/\//i;
const DOMAIN_OR_FILE = /[\p{L}\p{N}]\.[\p{L}]{2,}(?:[/:?#]|[.,;:!?)\]'"]*$)/u;
const ADJACENT_SKIP = /[\p{N}_@#/\\]/u;
const UPPER = /\p{Lu}/u;

/** Marks whose text is never spell checked: code, links and suggested removals (tracked deletions). */
export const SKIPPED_MARKS: ReadonlySet<string> = new Set(['code', 'link', 'trackedDeletion']);
/** Blocks whose text is never spell checked. */
export const SKIPPED_BLOCKS: ReadonlySet<string> = new Set(['codeBlock']);

function isUrlOrEmail(chunk: string): boolean {
  return chunk.includes('@') || SCHEME_URL.test(chunk) || /^www\./i.test(chunk) || DOMAIN_OR_FILE.test(chunk);
}

/** Acronyms (ALL-CAPS) and mixed-case product names (iPhone, SharePoint) are left alone. */
function isSkippedCasing(word: string): boolean {
  const core = word.replace(/'s$/i, '');
  if (core.length > 1 && core === core.toUpperCase() && core !== core.toLowerCase()) return true;
  return UPPER.test(core.slice(1));
}

/** Words worth checking in a plain string, skipping URLs, emails, acronyms and words joined to digits. */
export function tokenizeWords(text: string): WordToken[] {
  const blanked = text.replace(CHUNK, chunk => (isUrlOrEmail(chunk) ? ' '.repeat(chunk.length) : chunk));
  const tokens: WordToken[] = [];
  for (const match of blanked.matchAll(WORD)) {
    const word = match[0];
    const start = match.index ?? 0;
    const end = start + word.length;
    if (word.length < 2 || word.length > MAX_WORD_LENGTH) continue;
    if (ADJACENT_SKIP.test(blanked.charAt(start - 1)) || ADJACENT_SKIP.test(blanked.charAt(end))) continue;
    if (isSkippedCasing(word)) continue;
    tokens.push({ word: normaliseWord(word), start, end });
  }
  return tokens;
}

/**
 * The text of one text block with one character per document position, so offsets map to
 * `blockPos + 1 + offset`. Skipped marks and inline nodes (images, breaks) become separators.
 */
export function checkableBlockText(block: ProseMirrorNode): string {
  if (SKIPPED_BLOCKS.has(block.type.name) || block.type.spec.code) return '';
  let text = '';
  block.forEach(child => {
    if (child.isText && child.text) {
      const skipped = child.marks.some(mark => SKIPPED_MARKS.has(mark.type.name) || mark.type.spec.code);
      text += skipped ? ' '.repeat(child.text.length) : child.text;
    } else {
      text += ' '.repeat(child.nodeSize);
    }
  });
  return text;
}

/** Tokens of a text block at absolute document positions. */
export function blockWordTokens(block: ProseMirrorNode, blockPos: number): WordToken[] {
  const offset = blockPos + 1;
  return tokenizeWords(checkableBlockText(block)).map(token => ({ ...token, start: token.start + offset, end: token.end + offset }));
}
